/**
 * @file Runs a list of operation requests one at a time, reporting progress and collecting errors.
 */
import { wait } from "./wait";

type SetProgress = (percent: number) => void;

type BatchItem<Parameters> = {
  _rowIndex?: number;
  parameters: Parameters;
};

type OperationResponse<Data> = {
  data?: Data;
};

type OperationRequest<Parameters, Data> = (
  parameters: Parameters,
) => Promise<OperationResponse<Data>>;

type RequestError = {
  originalError: Error;
};

type OnError<Parameters> = (
  rowIndex: number,
  parameters: Parameters,
  originalError: Error,
) => unknown;

const BATCH_DELAY_MS = 1;
const PERCENT = 100;

/**
 * Sends each item's request in sequence, reporting progress and collecting results and errors.
 */
const requestBatch = async <Parameters, Data>(
  setProgress: SetProgress,
  parametersList: BatchItem<Parameters>[],
  operationRequest: OperationRequest<Parameters, Data>,
  onError: OnError<Parameters> = () => ({}),
): Promise<Array<Data | Error>> => {
  let operationCount = 1;
  const totalRequestsCount = parametersList.length + 1;

  const result: Array<Data | Error> = [];

  for (const { _rowIndex = operationCount, parameters } of parametersList) {
    const { data } = await operationRequest(parameters)
      // eslint-disable-next-line no-loop-func
      .catch(({ originalError }: RequestError): OperationResponse<Data> => {
        onError(_rowIndex, parameters, originalError);

        result.push(originalError);

        return {};
      });

    if (data) {
      result.push(data);
    }

    const percent = Math.ceil((operationCount * PERCENT) / totalRequestsCount);

    setProgress(percent);

    operationCount++;

    await wait(BATCH_DELAY_MS);
  }

  return result;
};

export default requestBatch;
